import { useState, useRef, useEffect } from "react"
import { Form } from "react-bootstrap"
import "./Typeahead.css"

/**
 * Bootstrap-style typeahead for searchable select.
 * @param {Object[]} options - List of options (objects with id and label fields, or use getOptionLabel)
 * @param {string} value - Selected option id (or null/empty)
 * @param {function} onChange - (optionId, option) => void
 * @param {function} getOptionLabel - (option) => string, default: option => option?.name ?? option?.label ?? ""
 * @param {string} placeholder - Input placeholder
 * @param {boolean} disabled - Disable the input
 * @param {boolean} isInvalid - Show error state
 * @param {string} size - "sm" | undefined
 * @param {string} className - Additional class for the wrapper
 */
const Typeahead = ({
    options = [],
    value,
    onChange,
    getOptionLabel = (option) => option?.name ?? option?.Name ?? option?.label ?? option?.Label ?? "",
    placeholder = "Search...",
    disabled = false,
    isInvalid = false,
    size,
    className = ""
}) => {
    const [query, setQuery] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(0)
    const wrapperRef = useRef(null)
    const listRef = useRef(null)

    const getOptionId = (opt) => opt?.id ?? opt?.Id
    const selectedOption = options?.find((opt) => String(getOptionId(opt)) === String(value)) ?? null
    const isSearching = query !== "" || !selectedOption
    const displayValue = isSearching ? query : getOptionLabel(selectedOption)

    const filteredOptions = (options || []).filter((opt) => {
        const label = getOptionLabel(opt)
        return label?.toLowerCase().includes((query || "").trim().toLowerCase())
    })

    // Reset highlighted index when filtered list changes
    useEffect(() => {
        setHighlightedIndex(0)
    }, [filteredOptions?.length, query])

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    // Scroll highlighted item into view
    useEffect(() => {
        if (!isOpen || highlightedIndex < 0) return
        const el = listRef.current?.children?.[highlightedIndex]
        el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }, [highlightedIndex, isOpen])

    const handleInputChange = (e) => {
        const newQuery = e.target.value
        setQuery(newQuery)
        if (!newQuery && value) {
            onChange("", null)
        }
        setIsOpen(true)
    }

    const handleInputFocus = () => {
        setIsOpen(true)
        setQuery(selectedOption ? getOptionLabel(selectedOption) : query)
    }

    const handleSelect = (option) => {
        onChange(getOptionId(option) ?? "", option)
        setQuery("")
        setIsOpen(false)
    }

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === "ArrowDown" || e.key === "Enter") {
                e.preventDefault()
                setIsOpen(true)
            }
            return
        }
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault()
                setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
                break
            case "ArrowUp":
                e.preventDefault()
                setHighlightedIndex((i) => Math.max(i - 1, 0))
                break
            case "Enter":
                e.preventDefault()
                if (filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex])
                }
                break
            case "Escape":
                setIsOpen(false)
                setQuery(selectedOption ? getOptionLabel(selectedOption) : "")
                break
            default:
                break
        }
    }

    const showDropdown = isOpen && !disabled
    const inputSizeClass = size === "sm" ? "form-control-sm" : ""

    return (
        <div ref={wrapperRef} className={`typeahead-wrapper ${className}`}>
            <Form.Control
                type="text"
                autoComplete="off"
                placeholder={placeholder}
                value={displayValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                className={`typeahead-input ${inputSizeClass} ${isInvalid ? "error" : ""}`}
                aria-expanded={showDropdown}
                aria-haspopup="listbox"
                role="combobox"
            />
            {showDropdown && (
                <ul
                    ref={listRef}
                    className="typeahead-dropdown"
                    role="listbox"
                    aria-label={placeholder}>
                    {filteredOptions.length === 0 ? (
                        <li className="typeahead-item typeahead-no-results">
                            No matches
                        </li>
                    ) : (
                        filteredOptions.map((option, index) => {
                            const label = getOptionLabel(option)
                            const displayText = label != null && String(label).trim() !== "" ? label : "(No name)"
                            return (
                                <li
                                    key={getOptionId(option) ?? index}
                                    role="option"
                                    aria-selected={index === highlightedIndex}
                                    className={`typeahead-item ${
                                        index === highlightedIndex ? "typeahead-item-active" : ""
                                    } ${String(getOptionId(option)) === String(value) ? "typeahead-selected" : ""}`}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                    onClick={() => handleSelect(option)}>
                                    {displayText}
                                </li>
                            )
                        })
                    )}
                </ul>
            )}
        </div>
    )
}

export default Typeahead
